/**
 * Le Conseil du Vigneron — quiz de recommandation de vin
 * Vignerons de la Cave de Pierrefeu
 *
 * Logique :
 * 1. Charge quiz-vigneron-data.json (les vins + leurs tags)
 * 2. Pose 4 questions obligatoires + 1 optionnelle
 * 3. Calcule un score par vin (voir SCORING dans le doc de conception)
 * 4. Relie chaque résultat à la vraie fiche produit Shopify via window.quizProductLookup
 *    (injecté par la section Liquid, voir sections/quiz-vigneron.liquid)
 */

(function () {
  const QUESTIONS = [
    {
      id: "moment",
      type: "single",
      question: "Quel moment souhaitez-vous que l'on accompagne ?",
      options: [
        { label: "Un apéritif convivial, entre amis", value: "aperitif" },
        { label: "Un moment de dégustation, pour prendre le temps", value: "degustation" },
        { label: "Un repas partagé, simple et généreux", value: "convivial" },
        { label: "Un repas raffiné ou une grande occasion", value: "gastronomique" },
        { label: "Un cadeau à offrir", value: "cadeau" }
      ]
    },
    {
      id: "couleur",
      type: "single",
      question: "Quelle couleur de vin vous tente ?",
      options: [
        { label: "Blanc", value: "blanc" },
        { label: "Rosé", value: "rose" },
        { label: "Rouge", value: "rouge" },
        { label: "Je ne sais pas, surprenez-moi", value: null }
      ]
    },
    {
      id: "accord",
      type: "single",
      question: "Qu'avez-vous prévu de déguster à côté ?",
      options: [
        { label: "Poisson, coquillages, cuisine de la mer", value: "poisson_fruits_de_mer" },
        { label: "Volaille, viande blanche", value: "viande_blanche" },
        { label: "Viande rouge, grillades", value: "viande_rouge" },
        { label: "Fromages affinés", value: "fromage" },
        { label: "Cuisine méditerranéenne, légumes de saison", value: "mediterraneen_legumes" },
        { label: "Cuisine exotique, épices, saveurs d'ailleurs", value: "cuisine_exotique" },
        { label: "Apéritif gourmand : tapas, planches, houmous", value: "apero_tapas" },
        { label: "Pas de plat particulier, juste pour le plaisir", value: "sans_plat_particulier" }
      ]
    },
    {
      id: "profil",
      type: "single",
      question: "Quel style de vin vous tente le plus ?",
      options: [
        { label: "Vif et rafraîchissant", value: "vif" },
        { label: "Rond et fruité", value: "rond" },
        { label: "Intense et structuré", value: "structure" },
        { label: "Je ne sais pas trop, je débute", value: null }
      ]
    },
    {
      id: "particularites",
      type: "multi",
      optional: true,
      question: "Un petit truc en plus qui vous ferait craquer ?",
      options: [
        { label: "Un vin bio", value: "bio" },
        { label: "Un vin à twister en cocktail", value: "vin_cocktail" },
        { label: "Un vin de garde, à conserver quelques années", value: "garde_longue" }
      ]
    }
  ];

  const FALLBACK_IDS = ["coteaux-blanc-2025", "coteaux-rose-2025", "coteaux-rouge-2024"];

  class VigneronQuiz {
    constructor(root) {
      this.root = root;
      this.dataUrl = root.dataset.dataUrl;
      this.answers = { moment: null, couleur: null, accord: null, profil: null, particularites: [] };
      this.currentStep = 0;
      this.wines = [];
      this.init();
    }

    async init() {
      try {
        const res = await fetch(this.dataUrl);
        const json = await res.json();
        this.wines = json.vins || [];
      } catch (e) {
        this.root.innerHTML = '<p class="vq-error">Le conseil du vigneron est momentanément indisponible.</p>';
        return;
      }
      this.render();
    }

    render() {
      this.root.innerHTML = `
        <div class="vq-intro">
          <p class="vq-eyebrow">Entre nous, à Pierrefeu</p>
          <h2 class="vq-title">${this.root.dataset.heading || "Le bon vin, au bon moment."}</h2>
          <p class="vq-subtitle">${this.root.dataset.subheading || "Répondez à quelques questions et laissez nos vignerons vous guider vers la cuvée idéale selon vos envies, votre repas et l'occasion."}</p>
        </div>
        <div class="vq-glass-wrap" aria-hidden="true">
          ${this.glassSvg()}
          <span class="vq-glass-label" data-vq-progress-label></span>
        </div>
        <div data-vq-steps></div>
      `;
      this.stepsEl = this.root.querySelector("[data-vq-steps]");
      this.glassFillEl = this.root.querySelector(".vq-glass-fill");
      this.progressLabelEl = this.root.querySelector("[data-vq-progress-label]");
      this.renderStep();
    }

    glassSvg() {
      return `
        <svg class="vq-glass" viewBox="0 0 34 46" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <clipPath id="vq-glass-clip">
              <path d="M7 4 L27 4 C27 15 23 23 17 27 C11 23 7 15 7 4 Z" />
            </clipPath>
          </defs>
          <path d="M7 4 L27 4 C27 15 23 23 17 27 C11 23 7 15 7 4 Z"
                fill="none" stroke="#45403D" stroke-width="1.3" stroke-linejoin="round" />
          <line x1="17" y1="27" x2="17" y2="40" stroke="#45403D" stroke-width="1.3" />
          <line x1="9" y1="41.5" x2="25" y2="41.5" stroke="#45403D" stroke-width="1.3" stroke-linecap="round" />
          <g clip-path="url(#vq-glass-clip)">
            <rect class="vq-glass-fill" x="5" y="4" width="24" height="26" fill="#F6C9BF" />
          </g>
        </svg>
      `;
    }

    updateProgress() {
      const requiredSteps = QUESTIONS.filter((q) => !q.optional).length;
      const answeredRequired = QUESTIONS.filter((q, i) => !q.optional && i < this.currentStep).length;
      const ratio = this.currentStep >= requiredSteps ? 1 : answeredRequired / requiredSteps;
      if (this.glassFillEl) {
        this.glassFillEl.style.transform = `translateY(${(1 - ratio) * 100}%)`;
      }
      if (this.progressLabelEl) {
        const shown = Math.min(this.currentStep + 1, requiredSteps);
        this.progressLabelEl.textContent =
          this.currentStep >= requiredSteps ? "Votre verre est prêt" : `Question ${shown} sur ${requiredSteps}`;
      }
    }

    renderStep() {
      this.updateProgress();

      if (this.currentStep >= QUESTIONS.length) {
        this.renderResults();
        return;
      }

      const q = QUESTIONS[this.currentStep];
      const isMulti = q.type === "multi";
      const optionsHtml = q.options
        .map((opt, i) => {
          const selected = isMulti
            ? this.answers[q.id].includes(opt.value)
            : this.answers[q.id] === opt.value;
          return `<button type="button" class="vq-option${selected ? " is-selected" : ""}"
                     data-vq-option data-value="${opt.value === null ? "" : opt.value}" data-index="${i}">
                     ${opt.label}
                   </button>`;
        })
        .join("");

      this.stepsEl.innerHTML = `
        <div class="vq-step is-active">
          <p class="vq-question">${q.question}</p>
          <div class="vq-options" role="group" aria-label="${q.question}">${optionsHtml}</div>
          <div class="vq-nav">
            <button type="button" class="vq-link-btn" data-vq-back ${this.currentStep === 0 ? "disabled style=visibility:hidden" : ""}>← Précédent</button>
            <button type="button" class="vq-primary-btn" data-vq-next ${isMulti ? "" : "disabled"}>
              ${q.optional ? "Voir mes vins" : "Suivant"}
            </button>
          </div>
          ${q.optional ? '<div class="vq-skip"><button type="button" class="vq-skip-btn" data-vq-skip>Sans préférence particulière</button></div>' : ""}
        </div>
      `;

      this.stepsEl.querySelectorAll("[data-vq-option]").forEach((btn) => {
        btn.addEventListener("click", () => this.selectOption(q, btn));
      });
      this.stepsEl.querySelector("[data-vq-next]").addEventListener("click", () => this.goNext());
      const backBtn = this.stepsEl.querySelector("[data-vq-back]");
      if (backBtn) backBtn.addEventListener("click", () => this.goBack());
      const skipBtn = this.stepsEl.querySelector("[data-vq-skip]");
      if (skipBtn) skipBtn.addEventListener("click", () => this.goNext());

      if (!isMulti && this.answers[q.id] !== null) {
        this.stepsEl.querySelector("[data-vq-next]").disabled = false;
      }
    }

    selectOption(q, btn) {
      const value = btn.dataset.value === "" ? null : btn.dataset.value;
      const nextBtn = this.stepsEl.querySelector("[data-vq-next]");

      if (q.type === "multi") {
        const list = this.answers[q.id];
        const idx = list.indexOf(value);
        if (idx > -1) list.splice(idx, 1);
        else list.push(value);
        btn.classList.toggle("is-selected");
      } else {
        this.answers[q.id] = value;
        this.stepsEl.querySelectorAll("[data-vq-option]").forEach((b) => b.classList.remove("is-selected"));
        btn.classList.add("is-selected");
        nextBtn.disabled = false;
      }
    }

    goNext() {
      this.currentStep += 1;
      this.renderStep();
      this.root.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }

    goBack() {
      this.currentStep -= 1;
      this.renderStep();
    }

    scoreWine(vin) {
      let score = 0;
      const a = this.answers;

      if (a.couleur && vin.couleur === a.couleur) score += 3;
      if (a.moment && (vin.quiz_moment || []).includes(a.moment)) score += 2;
      if (a.accord && (vin.quiz_accord || []).includes(a.accord)) score += 2;
      if (a.profil && vin.quiz_profil === a.profil) score += 1;

      a.particularites.forEach((p) => {
        if (p === "bio" && vin.bio) score += 2;
        if (p === "vin_cocktail" && vin.vin_cocktail) score += 2;
        if (p === "garde_longue" && vin.garde_longue) score += 2;
      });

      if (a.moment === "cadeau" && vin.cadeau_privilegie) score += 2;

      return score;
    }

    getTopWines() {
      let candidates = this.wines;
      if (this.answers.couleur) {
        candidates = candidates.filter((v) => v.couleur === this.answers.couleur);
      }
      if (!candidates.length) candidates = this.wines;

      // Filtre strict sur les particularités cochées : si on demande un bio,
      // on ne montre que les vins réellement bio (même si ça n'en fait qu'un).
      if (this.answers.particularites.length) {
        const strict = candidates.filter((v) =>
          this.answers.particularites.every((p) => v[p])
        );
        if (strict.length) candidates = strict;
      }

      const scored = candidates.map((v) => ({ vin: v, score: this.scoreWine(v) })).sort((x, y) => y.score - x.score);

      if (this.answers.couleur ? false : scored[0].score === 0) {
        return FALLBACK_IDS.map((id) => this.wines.find((v) => v.id === id)).filter(Boolean);
      }

      // Jusqu'à 3 résultats, mais seulement ceux qui correspondent vraiment :
      // on ne complète plus artificiellement avec des vins hors-critère.
      const top = scored.slice(0, 3);
      if (top.length > 1 && top[0].score - top[top.length - 1].score > 4) {
        while (top.length > 1 && top[0].score - top[top.length - 1].score > 4) top.pop();
      }
      return top.map((s) => s.vin);
    }

    renderResults() {
      const results = this.getTopWines();
      const lookup = window.quizProductLookup || {};

      const cardsHtml = results
        .map((vin) => {
          const product = lookup[vin.shopify_title] || {};
          const image = product.image
            ? `<img src="${product.image}" alt="${vin.nom}" loading="lazy">`
            : "";
          const cta = product.url
            ? `<a class="vq-result-cta" href="${product.url}">Voir la cuvée</a>`
            : "";
          return `
            <article class="vq-result-card">
              <div class="vq-result-image">${image}</div>
              <div class="vq-result-body">
                <p class="vq-result-words">${(vin.trois_mots || []).join(" · ")}</p>
                <h3 class="vq-result-name">${vin.nom}</h3>
                <p class="vq-result-note">${(vin.plats_suggeres || [])[0] || ""}</p>
                ${cta}
              </div>
            </article>
          `;
        })
        .join("");

      this.stepsEl.innerHTML = `
        <div class="vq-step is-active">
          <div class="vq-results-intro">
            <p class="vq-question">Le conseil du vigneron, pour vous</p>
          </div>
          <div class="vq-results-grid">${cardsHtml}</div>
          <div class="vq-restart">
            <button type="button" class="vq-link-btn" data-vq-restart">Recommencer le conseil</button>
          </div>
        </div>
      `;

      this.stepsEl.querySelector("[data-vq-restart]").addEventListener("click", () => {
        this.answers = { moment: null, couleur: null, accord: null, profil: null, particularites: [] };
        this.currentStep = 0;
        this.renderStep();
      });
    }
  }

  document.querySelectorAll("[data-vigneron-quiz]").forEach((el) => new VigneronQuiz(el));
})();